import { z } from 'zod';
import { emailSchema, phoneSchema } from './schemas.ts';

const blankToUndefined = <T extends z.ZodType>(s: T) => s.optional().or(z.literal('').transform(() => undefined));
const optText = (max: number) => blankToUndefined(z.string().trim().max(max));
const optUuid = z.uuid().nullable().optional().or(z.literal('').transform(() => null));

export const STAGES = ['new', 'contacted', 'qualified', 'negotiation', 'won', 'lost'] as const;
export type Stage = (typeof STAGES)[number];
export const STAGE_LABELS: Record<Stage, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Interested',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
};

export const CUSTOM_FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'select', 'checkbox', 'phone', 'email', 'url'] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

/** Fields people can type when adding or editing a lead. Name or phone is enough to start. */
export const leadFieldsSchema = z.object({
  name: optText(150),
  phone: blankToUndefined(phoneSchema),
  altPhone: blankToUndefined(phoneSchema),
  email: blankToUndefined(emailSchema),
  company: optText(150),
  city: optText(100),
  state: optText(100),
  address: optText(500),
  notes: optText(5000),
  value: z.coerce.number().min(0).max(1e12).nullable().optional().or(z.literal('').transform(() => null)),
  isQualified: z.boolean().optional(),
  statusId: optUuid,
  sourceId: optUuid,
  campaignId: optUuid,
  ownerId: optUuid,
  tagIds: z.array(z.uuid()).max(50).optional(),
  custom: z.record(z.string(), z.unknown()).optional(),
});

export const createLeadSchema = leadFieldsSchema
  .extend({
    nextFollowupAt: z.iso.datetime().nullable().optional(),
    followupNote: optText(500),
    /** Set after the user has seen the duplicate warning and chose to add anyway. */
    allowDuplicate: z.boolean().optional(),
  })
  .refine((v) => v.name || v.phone || v.email, { error: 'Enter at least a name, phone number or email.', path: ['name'] });

export const updateLeadSchema = leadFieldsSchema;

export const noteSchema = z.object({ body: z.string().trim().min(1, { error: 'Write something first.' }).max(5000) });

export const followupSchema = z.object({
  at: z.iso.datetime({ error: 'Pick a date and time.' }),
  note: optText(500),
});

export const followupDoneSchema = z.object({
  /** What happened on this follow-up, saved to the timeline. */
  note: optText(2000),
  statusId: optUuid,
  /** Optional next follow-up so the lead never falls through the cracks. */
  nextAt: z.iso.datetime().nullable().optional(),
  nextNote: optText(500),
});

export const bulkLeadSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('assign'), ids: z.array(z.uuid()).min(1).max(1000), ownerId: z.uuid().nullable() }),
  z.object({ action: z.literal('status'), ids: z.array(z.uuid()).min(1).max(1000), statusId: z.uuid() }),
  z.object({ action: z.literal('campaign'), ids: z.array(z.uuid()).min(1).max(1000), campaignId: z.uuid().nullable() }),
  z.object({ action: z.literal('addTag'), ids: z.array(z.uuid()).min(1).max(1000), tagId: z.uuid() }),
  z.object({ action: z.literal('removeTag'), ids: z.array(z.uuid()).min(1).max(1000), tagId: z.uuid() }),
  z.object({ action: z.literal('delete'), ids: z.array(z.uuid()).min(1).max(1000) }),
]);

const csv = z
  .string()
  .optional()
  .transform((v) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []));
const uuidCsv = csv.pipe(z.array(z.uuid()).max(100));

export const leadListQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  status: uuidCsv,
  stage: csv.pipe(z.array(z.enum(STAGES))),
  source: uuidCsv,
  campaign: uuidCsv,
  tag: uuidCsv,
  /** User ids, or "none" for unassigned. */
  owner: csv.pipe(z.array(z.union([z.uuid(), z.literal('none')])).max(100)),
  followup: z.enum(['overdue', 'today', 'upcoming', 'none']).optional(),
  qualified: z.enum(['yes', 'no']).optional(),
  /** AI lead temperature. */
  heat: z.enum(['hot', 'warm', 'cold']).optional(),
  createdFrom: z.iso.date().optional(),
  createdTo: z.iso.date().optional(),
  sort: z.enum(['newest', 'oldest', 'followup', 'activity', 'name', 'score']).default('newest'),
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  pageSize: z.coerce.number().int().min(10).max(200).default(50),
});
export type LeadListQuery = z.input<typeof leadListQuerySchema>;

/* Lead setup (settings) ------------------------------------------------------------------ */

const setupName = z.string().trim().min(1, { error: 'Please enter a name.' }).max(60);
const color = z.string().regex(/^#[0-9a-fA-F]{6}$/, { error: 'Pick a colour.' });

export const statusSchema = z.object({ name: setupName, color, stage: z.enum(STAGES) });
export const sourceSchema = z.object({ name: setupName, isActive: z.boolean().default(true) });
export const tagSchema = z.object({ name: setupName, color });
export const campaignSchema = z.object({ name: setupName, description: optText(500), isActive: z.boolean().default(true) });
export const customFieldSchema = z
  .object({
    label: z.string().trim().min(1, { error: 'Please enter a label.' }).max(60),
    type: z.enum(CUSTOM_FIELD_TYPES),
    options: z.array(z.string().trim().min(1).max(60)).max(100).default([]),
    isRequired: z.boolean().default(false),
    showInList: z.boolean().default(false),
  })
  .refine((v) => v.type !== 'select' || v.options.length > 0, { error: 'Add at least one choice.', path: ['options'] });
export const assignmentRuleSchema = z.object({
  name: setupName,
  sourceId: optUuid,
  campaignId: optUuid,
  userIds: z.array(z.uuid()).min(1, { error: 'Pick at least one person.' }).max(200),
  isActive: z.boolean().default(true),
});

/** Rows sent by the import screen, already mapped to lead fields. */
export const importRowSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));
export const importSchema = z.object({
  rows: z.array(importRowSchema).min(1).max(500),
  options: z.object({
    statusId: optUuid,
    sourceId: optUuid,
    campaignId: optUuid,
    assign: z.enum(['me', 'auto', 'user', 'none']).default('auto'),
    ownerId: optUuid,
    duplicates: z.enum(['skip', 'update', 'add']).default('skip'),
    tagIds: z.array(z.uuid()).max(20).default([]),
  }),
});
